import type {
  ModifiedPlayer,
  NFLGameInputs,
  NFLTeamInputs,
  SimulationRun
} from '@library/types/simulations';
import { useMemo, useState } from 'react';
import type { GameData } from '@library/types/games';
import type { LeagueTeamPlayer } from '@library/types/player';
import type { Updater } from 'use-immer';
import { partition } from 'ramda';
import { useImmer } from 'use-immer';
import { useSimulations } from '@bo-hooks/simulations/useSimulations';

export interface SimulationBuilder {
  scenarioName: string;
  scenarioNameDefault: string;
  setScenarioName: (name: string) => void;

  gameOverrides: NFLGameInputs;
  setGameOverrides: (overrides: NFLGameInputs) => void;

  homeTeamInputs: NFLTeamInputs;
  setHomeTeamInputs: (inputs: NFLTeamInputs) => void;

  awayTeamInputs: NFLTeamInputs;
  setAwayTeamInputs: (inputs: NFLTeamInputs) => void;

  modifiedPlayers: Array<ModifiedPlayer>;
  setModifiedPlayers: Updater<Array<ModifiedPlayer>>;

  removedPlayers: Array<Omit<ModifiedPlayer, 'playerOverrides'>>;
  setRemovedPlayers: Updater<Array<Omit<ModifiedPlayer, 'playerOverrides'>>>;

  homeTeamPlayers: Array<LeagueTeamPlayer>;
  awayTeamPlayers: Array<LeagueTeamPlayer>;

  defaultPlayers: Array<ModifiedPlayer>;
}

export function useSimulationBuilder({
  defaultSimulation,
  game,
  gamePlayers,
  leagueId,
  simulation
}: {
  defaultSimulation: SimulationRun;
  game: GameData;
  gamePlayers: Array<LeagueTeamPlayer>;
  leagueId: string;
  simulation?: SimulationRun;
}): SimulationBuilder {
  const { simulations = [] } = useSimulations({
    gameId: game.id,
    leagueId
  });

  const [scenarioName, setScenarioName] = useState(
    simulation?.data?.attributes.simulation_run_name || ''
  );
  const scenarioNameDefault =
    simulation?.data?.attributes.simulation_run_name ||
    `${game.home_team_name} vs. ${
      game.away_team_name
    } @ ${game.scheduled_at} - ${
      simulations?.length > 0 ? simulations?.length : ''
    }`;

  const [gameOverrides, setGameOverrides] = useState<NFLGameInputs>(
    simulation?.data?.attributes.simulation_run_inputs.game_inputs ||
      defaultSimulation.data.attributes.simulation_run_inputs.game_inputs
  );

  const [homeTeamInputs, setHomeTeamInputs] = useState<NFLTeamInputs>(
    simulation?.data?.attributes.simulation_run_inputs.team_inputs?.[0] ||
      defaultSimulation.data.attributes.simulation_run_inputs.team_inputs?.[0]
  );

  const [awayTeamInputs, setAwayTeamInputs] = useState<NFLTeamInputs>(
    simulation?.data?.attributes.simulation_run_inputs.team_inputs?.[1] ||
      defaultSimulation.data.attributes.simulation_run_inputs.team_inputs?.[1]
  );

  /*
FOR INGESTING PLAYER INPUTS - will need to noodle on this more

  const [defaultPlayerInputs, setDefaultPlayerInputs] = useState(
    simulation?.data?.attributes.simulation_run_inputs.player_inputs
  );
*/

  const defaultPlayers = useMemo<Array<ModifiedPlayer>>(() => {
    return defaultSimulation.data.attributes.simulation_run_inputs.player_inputs.flatMap(
      (team) => {
        return team.players.map((simPlayerInputs) => {
          const connectedSimPlayer = gamePlayers.find(
            (player) =>
              player.name.toLocaleUpperCase() ===
              simPlayerInputs.player_name.toLocaleUpperCase()
          );

          return {
            foreignId: connectedSimPlayer?.foreign_id || '',
            homeTeam: connectedSimPlayer?.team_name === game.home_team_name,
            playerId: connectedSimPlayer?.foreign_id || '',
            playerName: connectedSimPlayer?.name || '',
            playerOverrides: simPlayerInputs
          };
        });
      }
    );
  }, [game.home_team_name, gamePlayers, defaultSimulation]);

  const teamOneExistingInputs: Array<ModifiedPlayer> =
    simulation?.data?.attributes.simulation_run_inputs.player_inputs[0].players.flatMap(
      (simPlayerInputs) => {
        const connectedSimPlayer = gamePlayers.find(
          (player) => player.foreign_id === simPlayerInputs.prizepicks_player_id
        );

        return {
          foreignId: connectedSimPlayer?.foreign_id || '',
          homeTeam: connectedSimPlayer?.team_name === game.home_team_name,
          playerId: connectedSimPlayer?.foreign_id || '',
          playerName: connectedSimPlayer?.name || '',
          playerOverrides: simPlayerInputs
        };
      }
    ) || [];

  const teamTwoExistingInputs: Array<ModifiedPlayer> =
    simulation?.data?.attributes.simulation_run_inputs.player_inputs[1].players.flatMap(
      (simPlayerInputs) => {
        const connectedSimPlayer = gamePlayers.find(
          (player) => player.foreign_id === simPlayerInputs.prizepicks_player_id
        );

        const defaultPlayer = defaultPlayers.find(
          (player) => player.foreignId === connectedSimPlayer?.foreign_id
        );

        if (!connectedSimPlayer || !defaultPlayer) {
          return [];
        }

        return {
          foreignId: connectedSimPlayer?.foreign_id || '',
          homeTeam: connectedSimPlayer?.team_name === game.home_team_name,
          playerId: connectedSimPlayer?.foreign_id || '',
          playerName: connectedSimPlayer?.name || '',
          playerOverrides: simPlayerInputs
        };
      }
    ) || [];

  const initialModifiedPlayers =
    teamOneExistingInputs.length > 0 || teamTwoExistingInputs.length > 0
      ? [...teamOneExistingInputs, ...teamTwoExistingInputs]
      : defaultPlayers;

  const [modifiedPlayers, setModifiedPlayers] = useImmer<Array<ModifiedPlayer>>(
    initialModifiedPlayers
  );

  const modifiedRemovedPlayers = useMemo<
    Array<Omit<ModifiedPlayer, 'playerOverrides'>>
  >(() => {
    if (!simulation) {
      return [];
    }

    return simulation.data.attributes.simulation_run_inputs.removed_players.flatMap(
      (team) => {
        return team.players.map((simPlayerInputs) => {
          const connectedSimPlayer = gamePlayers.find(
            (player) =>
              player.foreign_id === simPlayerInputs.prizepicks_player_id
          );

          return {
            foreignId: connectedSimPlayer?.foreign_id || '',
            homeTeam: connectedSimPlayer?.team_name === game.home_team_name,
            playerId: connectedSimPlayer?.foreign_id || '',
            playerName: connectedSimPlayer?.name || ''
          };
        });
      }
    );
  }, [game.home_team_name, gamePlayers, simulation]);

  const defaultRemovedPlayers = useMemo<
    Array<Omit<ModifiedPlayer, 'playerOverrides'>>
  >(() => {
    return defaultSimulation.data.attributes.simulation_run_inputs.removed_players.flatMap(
      (team) => {
        return team.players.map((simPlayerInputs) => {
          const connectedSimPlayer = gamePlayers.find(
            (player) =>
              player.foreign_id === simPlayerInputs.prizepicks_player_id
          );

          return {
            foreignId: connectedSimPlayer?.foreign_id || '',
            homeTeam: connectedSimPlayer?.team_name === game.home_team_name,
            playerId: connectedSimPlayer?.foreign_id || '',
            playerName: connectedSimPlayer?.name || ''
          };
        });
      }
    );
  }, [game.home_team_name, gamePlayers, defaultSimulation]);

  const [removedPlayers, setRemovedPlayers] = useImmer<
    Array<Omit<ModifiedPlayer, 'playerOverrides'>>
  >(
    modifiedRemovedPlayers.length > 0
      ? modifiedRemovedPlayers
      : defaultRemovedPlayers
  );

  const [homeTeamPlayers, awayTeamPlayers] = partition(
    (player) => player.team_name === game.home_team_name,
    gamePlayers
  );

  return {
    scenarioName,
    scenarioNameDefault,
    setScenarioName,

    gameOverrides,
    setGameOverrides,

    homeTeamInputs,
    setHomeTeamInputs,

    awayTeamInputs,
    setAwayTeamInputs,

    modifiedPlayers,
    setModifiedPlayers,

    removedPlayers,
    setRemovedPlayers,

    homeTeamPlayers,
    awayTeamPlayers,

    defaultPlayers
  };
}
